import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { environement } from 'src/environments/environment';

const LS_ACCESS = 'gt_access_token';
const LS_REFRESH = 'gt_refresh_token';

function decodeJwtPayload(
	token: string,
): { exp?: number; sub?: string; email?: string } | null {
	try {
		const part = token.split('.')[1];
		const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
		return JSON.parse(json);
	} catch {
		return null;
	}
}

@Injectable({ providedIn: 'root' })
export class ApiService {
	private readonly baseUrl = environement.API_URL.replace(/\/$/, '');

	private tokenSig = signal<string | null>(localStorage.getItem(LS_ACCESS));

	constructor(private http: HttpClient) {}

	private url(path: string): string {
		const p = path.startsWith('/') ? path : `/${path}`;
		return `${this.baseUrl}${p}`;
	}

	isAuthenticated = computed(() => {
		this.tokenSig();
		const t = this.getAccessToken();
		if (!t) {
			return false;
		}
		const payload = decodeJwtPayload(t);
		if (!payload?.exp) {
			return true;
		}
		return payload.exp * 1000 > Date.now();
	});

	getAccessToken(): string | null {
		return this.tokenSig() ?? localStorage.getItem(LS_ACCESS);
	}

	getRefreshToken(): string | null {
		return localStorage.getItem(LS_REFRESH);
	}

	setTokens(accessToken: string, refreshToken: string): void {
		localStorage.setItem(LS_ACCESS, accessToken);
		localStorage.setItem(LS_REFRESH, refreshToken);
		this.tokenSig.set(accessToken);
	}

	clearTokens(): void {
		localStorage.removeItem(LS_ACCESS);
		localStorage.removeItem(LS_REFRESH);
		this.tokenSig.set(null);
	}

	get<T>(path: string) {
		return this.http.get<T>(this.url(path));
	}

	post<T>(path: string, body: unknown) {
		return this.http.post<T>(this.url(path), body);
	}

	patch<T>(path: string, body: unknown) {
		return this.http.patch<T>(this.url(path), body);
	}

	delete<T>(path: string) {
		return this.http.delete<T>(this.url(path));
	}

	postFormData<T>(path: string, data: FormData) {
		return this.http.post<T>(this.url(path), data);
	}
}
